import React, { useEffect, useMemo, useState } from "react";
import {
  getGroupMessages,
  deleteMessage,
  createMessage,
} from "./../../../services/chatServices";
import { getGroupsList } from "./../../../services/groupServices";
import { getAdherentsByUser } from "./../../../services/userServices";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Button from "./../../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";
import { FaPaperPlane } from "react-icons/fa";
import MessageBubble from "./MessageBubble";

const BG_THEMES = [
  { id: "slate", name: "Neutre", area: "bg-slate-100/60 dark:bg-slate-900/40" },
  {
    id: "sky",
    name: "Bleu pastel",
    area: "bg-sky-100/70 dark:bg-slate-900/40",
  },
  {
    id: "emerald",
    name: "Vert pastel",
    area: "bg-emerald-100/70 dark:bg-slate-900/40",
  },
  {
    id: "rose",
    name: "Rose pastel",
    area: "bg-rose-100/70 dark:bg-slate-900/40",
  },
  {
    id: "violet",
    name: "Violet pastel",
    area: "bg-violet-100/70 dark:bg-slate-900/40",
  },
  {
    id: "amber",
    name: "Pêche pastel",
    area: "bg-amber-100/70 dark:bg-slate-900/40",
  },
];

export default function ChatPage() {
  useScrollToTop();
  const { user } = useUser();
  const userId = user?.userId;
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  // Groupes
  const [groups, setGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState("");

  // Messages
  const [messages, setMessages] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Composer
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Thème fond pastel
  const [bgTheme, setBgTheme] = useState("slate");
  const chatAreaBgClass = useMemo(() => {
    const found = BG_THEMES.find((t) => t.id === bgTheme);
    return found ? found.area : BG_THEMES[0].area;
  }, [bgTheme]);

  // --------- Chargement des groupes
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingGroups(true);
        if (isManager) {
          const list = await getGroupsList();
          setAllGroups(Array.isArray(list) ? list : []);
          if (Array.isArray(list) && list.length > 0) {
            setSelectedGroupId(list[0].id);
            setSelectedGroupName(list[0].nom);
          }
        } else {
          const adherents = await getAdherentsByUser(userId);
          const uniq = [];
          adherents?.forEach((a) => {
            a?.Groups?.forEach((g) => {
              if (!uniq.find((x) => x.id === g.id)) {
                uniq.push({
                  id: g.id,
                  nom: g.nom,
                  adherentNom: `${a.prenom} ${a.nom}`,
                });
              }
            });
          });
          setGroups(uniq);
          if (uniq.length > 0) {
            setSelectedGroupId(uniq[0].id);
            setSelectedGroupName(uniq[0].nom);
          }
        }
      } catch (e) {
        console.error("Erreur chargement groupes:", e);
        toast.error("Impossible de charger les groupes.");
      } finally {
        setLoadingGroups(false);
      }
    };
    load();
  }, [isManager, userId]);

  // --------- Chargement des messages + polling (aucun auto-scroll)
  useEffect(() => {
    if (!selectedGroupId) return;

    let interval;
    const fetchMessages = async () => {
      try {
        setLoadingMessages(true);
        const msgs = await getGroupMessages(selectedGroupId);
        // Tri DECROISSANT → les plus récents EN HAUT
        const sorted = (msgs || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setMessages(
          sorted.map((m) => ({
            ...m,
            currentUserId: userId,
          }))
        );
      } catch (e) {
        console.error("Erreur chargement messages:", e);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
    interval = setInterval(fetchMessages, 5000);

    return () => clearInterval(interval);
  }, [selectedGroupId, userId]);

  // --------- Changement de groupe
  const groupOptions = useMemo(
    () => (isManager ? allGroups : groups),
    [isManager, allGroups, groups]
  );
  const handleGroupChange = (e) => {
    const id = Number(e.target.value);
    setSelectedGroupId(id);
    const found = groupOptions.find((g) => g.id === id);
    setSelectedGroupName(found?.nom || "");
  };

  // --------- Envoi d’un message (on ajoute EN HAUT, pas de scroll)
  const handleSendMessage = async (ev) => {
    ev?.preventDefault?.();
    const txt = message?.trim();
    if (!selectedGroupId) {
      toast.warn("Sélectionne un groupe avant d’écrire.");
      return;
    }
    if (!txt) return;

    try {
      setSending(true);

      // Optimistic UI — insertion en tête (top)
      const temp = {
        id: `tmp-${Date.now()}`,
        user_id: userId,
        message: txt,
        createdAt: new Date().toISOString(),
        Users: {
          prenom: user?.prenom || "Moi",
          nom: user?.nom || "",
          photoURL: user?.photoURL || "",
        },
        currentUserId: userId,
        _optimistic: true,
      };
      setMessages((prev) => [temp, ...prev]);
      setMessage("");

      const saved = await createMessage({
        group_id: selectedGroupId,
        user_id: userId,
        message: txt,
      });

      if (saved?.id) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === temp.id ? { ...saved, currentUserId: userId } : m
          )
        );
      }
    } catch (e) {
      console.error("Envoi message échoué:", e);
      toast.error("Échec de l’envoi du message.");
      setMessages((prev) => prev.filter((m) => !m._optimistic));
      setMessage(txt);
    } finally {
      setSending(false);
    }
  };

  // --------- Suppression
  const handleDeleteMessage = async (id) => {
    if (!id) return;
    try {
      await deleteMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      console.error("Suppression échouée:", e);
      toast.error("Impossible de supprimer ce message.");
    }
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Utilisateur"
          title="Discussion entre Groupes"
          align="center"
          className="mb-1"
        />

        {/* Nav */}
        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Header gradient */}
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold">
                  Chat du club
                </h3>
                <p className="text-white/90 text-sm">
                  ⚠️Rappel : respect, pas de politique ni religion, pas de données
                  personnelles, langage correct — conforme au Code Moral de
                  votre discipline.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {/* Sélecteur de groupe */}
                <div>
                  <label className="block text-xs font-semibold mb-1 ml-1">
                    {isManager
                      ? "Sélection du groupe"
                      : "Groupe de votre/vos adhérents"}
                  </label>
                  <select
                    disabled={loadingGroups || groupOptions.length === 0}
                    onChange={handleGroupChange}
                    value={selectedGroupId || ""}
                    className="w-full rounded-xl border border-white/50 bg-white/90 text-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/70 disabled:opacity-60"
                  >
                    {groupOptions.length === 0 && (
                      <option value="">Aucun groupe</option>
                    )}
                    {groupOptions.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.nom}
                        {!isManager && g.adherentNom
                          ? ` (${g.adherentNom})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Fond pastel */}
                <div>
                  <label className="block text-xs font-semibold mb-1 ml-1">
                    Fond des messages
                  </label>
                  <select
                    value={bgTheme}
                    onChange={(e) => setBgTheme(e.target.value)}
                    className="w-full rounded-xl border border-white/50 bg-white/90 text-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/70"
                    aria-label="Choix du fond de la zone de discussion"
                  >
                    {BG_THEMES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Corps : liste + composer */}
          <div className="p-4 sm:p-6">
            <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-800/70 bg-white/70 dark:bg-slate-900/70 overflow-hidden flex flex-col">
              {/* En-tête du groupe */}
              <div className="px-4 py-3 bg-slate-50/70 dark:bg-slate-900/70 border-b border-slate-200/60 dark:border-slate-800/60">
                <h4 className="text-slate-900 dark:text-slate-100 font-semibold">
                  Groupe actuel :{" "}
                  <span className="font-bold">{selectedGroupName || "—"}</span>
                </h4>
              </div>

              {/* Liste des messages — zone FIXE, scroll interne, derniers messages en HAUT */}
              <div
                className={`flex-1 min-h-[20vh] max-h-[40vh] sm:max-h-[45vh] overflow-y-auto px-2 sm:px-4 py-4 ${chatAreaBgClass}`}
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {loadingMessages && (
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-14 rounded-xl bg-slate-200/60 dark:bg-slate-800/60 animate-pulse"
                      />
                    ))}
                  </div>
                )}

                {!loadingMessages && messages.length === 0 && (
                  <div className="text-center py-8 text-slate-600 dark:text-slate-300">
                    Aucun message pour l’instant. Démarre la conversation ✨
                  </div>
                )}

                {!loadingMessages && (
                  <div className="space-y-2">
                    {messages.map((msg) => (
                      <MessageBubble
                        key={msg.id}
                        message={msg}
                        canDelete={userId === msg.user_id || isManager}
                        onDeleteMessage={() => handleDeleteMessage(msg.id)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Composer (aucun auto-scroll) */}
              <form
                onSubmit={handleSendMessage}
                className="sticky bottom-0 flex items-center gap-2 px-3 sm:px-4 py-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-t border-slate-200/70 dark:border-slate-800/70"
              >
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                  className="flex-1 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                  placeholder={
                    selectedGroupId
                      ? "Écrire un message…"
                      : "Sélectionnez d’abord un groupe"
                  }
                  disabled={!selectedGroupId || sending}
                  aria-label="Message"
                />
                <Button
                  type="submit"
                  text={sending ? "Envoi…" : "Envoyer"}
                  icon={FaPaperPlane}
                  className="!rounded-xl gap-2"
                  disabled={!selectedGroupId || sending || !message.trim()}
                />
              </form>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
}
